import React from 'react';
import ConfirmDialog from '../ConfirmDialog';
import { BTN, BTN_XS } from '../../constants/buttons';
import { UNSAVED_PROMPT_BUTTONS } from '../../constants/unsavedChangesPrompt';
import { ROW_HOVER } from '../../constants/rowStyles';
import { statusPillClass } from '../ui/StatusPill';
import {
  InlineEditPanelRow,
  KebabMenu,
  EXPANDED_ROW_FRAME,
  ROW_EDIT_BAR_CLASS,
  ROW_EDIT_BAR_LABEL_CLASS,
  ROW_EDIT_UNSAVED_CHIP_CLASS,
} from '../TableRowExpansion';

/**
 * THE in-place row editing of the Manage Budgets tables — the GAG BREAKDOWN's
 * model (2026-10-25, owner: *"the inputs should show in the collpased section
 * and become editbale when cliked on so that the lable becomes the input just
 * like breakdown with additional details expanding underneath"* and *"you
 * should be able to use common componetns lik eon breakdown"*). Paired with
 * `hooks/useExpandRowEditor.js`, which owns the state; these render it.
 *
 * 🔀 REPLACES the 2026-10-24 model, which took the CREW MEMBERS table as its
 * reference — the row stayed text and every input opened in a panel UNDER it.
 * That was the controller's wrong reference, not the owner's ask. Now:
 * → the three parts (row cells, `RowEditBar`, the panel): `components/budget/CLAUDE.md` (Manage Budgets), moved there 2026-10-28.
 *
 * Shared pieces, not look-alikes, all in `components/TableRowExpansion.js`:
 * the Breakdown's DESKTOP chevron button (`RowToggleChevron`) and panel row
 * (`InlineEditPanelRow`), moved there verbatim; `EXPANDED_ROW_FRAME`; the bar
 * classes; and the phone cards' `ExpandChevron` (the Breakdown phone row's).
 * `ROW_HOVER` is `constants/rowStyles`. Visible differences from a Breakdown
 * row, deliberate: no spacer/gap-mask rows around the open pair (they paint
 * the PAGE colour over a group card's borders; these tables have no card).
 *
 * → the phone cards and "resolves nothing": `components/budget/CLAUDE.md` (Manage Budgets), moved there 2026-10-28.
 */

/** A row cell at rest, and the same cell while its row is edited in place. */
export const ROW_CELL_PAD = 'px-3 py-2';
export const ROW_CELL_PAD_EDIT = 'px-1 py-1';

/**
 * The summary-row frame: the shared expanded frame (`[&>td]` tint, hover step,
 * blue top edge) or the collapsed hover, plus the dirty-guard `opacity-40` on
 * the OTHER rows (that opacity meaning is spoken for — `components/budget/CLAUDE.md`).
 */
// `transition-[background-color,opacity]`: the dirty-guard dim FADES (as these
// rows' `transition-opacity` always did — review M5) and the hover tint eases too.
export const editableRowClass = ({ expanded, dimmed }) =>
  `cursor-pointer transition-[background-color,opacity] ${expanded ? EXPANDED_ROW_FRAME : ROW_HOVER} ${dimmed ? 'opacity-40' : ''}`;

/** The phone card panel's 200ms opacity fade. */
export const rowPanelFadeClass = (fading) =>
  `transition-opacity duration-200 ease-out ${fading ? 'opacity-0' : 'opacity-100'}`;

/** Stop ANY click inside this element from toggling the row (button clusters, the bar). */
export const stopRowToggle = (e) => e.stopPropagation();

/**
 * The wrapper around ONE in-row input: stop the toggle only when the click is
 * ON a control. The money inputs are a fixed 132px right-aligned in a wider
 * column, so a whole-cell stop left a dead strip beside them where a row click
 * did nothing (owner 2026-10-25: "does not close all the time"). The Breakdown
 * never had the strip — its inputs fill their cells — so this restores its
 * rule: the open row's non-control area closes it.
 */
export const ROW_CONTROL_SELECTOR = 'input, select, textarea, button, a, label, [role="combobox"], [role="listbox"], [role="option"]';
export const stopRowToggleOnControl = (e) => {
  if (e.target?.closest?.(ROW_CONTROL_SELECTOR)) e.stopPropagation();
};

/**
 * The Breakdown's "Editing this row" bar, directly under the row it saves.
 * 🔑 Save is DISABLED while clean, as the Breakdown's is (coordinator ruling
 * 2026-10-25) — an untouched Save used to PUT the stored values back; now it
 * sends nothing. Every body for a REAL edit is unchanged.
 * `actions` (optional node, 2026-11-03 — the Users table's `Actions ▾`, which
 * the approved mock puts ON this bar) renders before Cancel; absent, the bar
 * is byte-identical (`ExpandRowParts.rowEditBarActions.test.js`). `saveTitle` /
 * `cancelTitle` (optional) are the buttons' native tooltips (the owner's
 * tooltip ruling); undefined renders no `title` attribute at all.
 */
export function RowEditBar({ onSave, onCancel, saving, dirty, testIdPrefix, actions = null, saveTitle, cancelTitle }) {
  return (
    <div className={ROW_EDIT_BAR_CLASS} data-testid={`${testIdPrefix}-bar`} onClick={stopRowToggle}>
      <span className={ROW_EDIT_BAR_LABEL_CLASS}>Editing this row</span>
      {dirty && (
        <span className={`inline-flex ${statusPillClass()} ${ROW_EDIT_UNSAVED_CHIP_CLASS}`} data-testid={`${testIdPrefix}-dirty`}>
          ⚠ Unsaved changes
        </span>
      )}
      <span className="ml-auto flex items-center gap-2">
        {actions}
        <button type="button" onClick={onCancel} disabled={saving} data-testid={`${testIdPrefix}-cancel`} title={cancelTitle} className={BTN_XS.secondary}>
          Cancel
        </button>
        <button type="button" onClick={onSave} disabled={saving || !dirty} data-testid={`${testIdPrefix}-save`} title={saveTitle} className={BTN_XS.confirm}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </span>
    </div>
  );
}

const UP_PATH = 'M5 15l7-7 7 7';
const DOWN_PATH = 'M19 9l-7 7-7-7';

/**
 * THE ↑/↓ reorder pair of every Manage Budgets list (2026-10-25 — it was
 * pasted nine times). Owner: Payment Types "almost unusable"; measured, the
 * STACKED 20px pair made a row 48–49px while a first/last row (one arrow) was
 * 37–39px, so heights jumped row to row. Now:
 *   - `cell` (desktop table): the pair sits SIDE BY SIDE (16×22 each, so it
 *     fits the existing 40px column inside the cell's px-1 — a user's stored
 *     Cost Codes width is never too narrow) and is shorter than a text row,
 *     so it never sets the row's height.
 *   - `card` (phone list): the pre-existing 44px side-by-side targets below
 *     `md`, stacked 22px above it — byte-for-byte the classes the copies had.
 *   - BOTH slots always render; a missing direction is an invisible spacer, so
 *     every row's arrows line up in the same place.
 * The wrapper stops the row toggle (a click between the arrows does nothing).
 */
export function RowReorderButtons({ index, count, onMove, layout = 'cell' }) {
  const card = layout === 'card';
  const btn = card
    ? 'text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 p-1 min-h-[22px] min-w-[22px] max-md:min-h-[44px] max-md:min-w-[44px] flex items-center justify-center'
    : 'text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded w-4 h-[22px] flex items-center justify-center transition-colors';
  const slot = (dir, show, label, d) => (show ? (
    <button type="button" onClick={(e) => { e.stopPropagation(); onMove(index, dir); }} className={btn} aria-label={label} data-reorder-btn>
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d={d} /></svg>
    </button>
  ) : <span aria-hidden="true" className={btn.replace(/hover:\S+ /g, '')} data-reorder-spacer />);
  return (
    <div
      className={card ? 'flex flex-col gap-0.5 flex-shrink-0 max-md:flex-row max-md:gap-0' : 'flex items-center justify-center'}
      onClick={stopRowToggle}
    >
      {slot(-1, index > 0, 'Move up', UP_PATH)}
      {slot(1, index < count - 1, 'Move down', DOWN_PATH)}
    </div>
  );
}

/**
 * The row's ⋯ menu cell — the Breakdown's own `KebabMenu` (owner ruling A,
 * 2026-10-25: Delete lives in the row menu, as on the Breakdown, so a table
 * with no extra fields opens no empty panel). Its own 32px column, after the
 * reorder pair. The cell stops the row toggle.
 */
export const ROW_MENU_COL_WIDTH = 32;
export function RowMenuCell({ items, label }) {
  return (
    <td className="px-0.5 py-1 text-center" onClick={stopRowToggle} data-col="row_menu">
      <KebabMenu items={items} label={label} />
    </td>
  );
}

/** Delete, at the foot of the expanded details (where it has always lived). */
export function RowDeleteButton({ onDelete, label, testId }) {
  return (
    <div className="pt-3">
      <button type="button" onClick={onDelete} data-testid={testId} className={BTN_XS['danger-soft']}>
        {label}
      </button>
    </div>
  );
}

/**
 * The desktop expansion under an in-place-edited row: the Breakdown's own
 * desktop panel row (`InlineEditPanelRow`, sticky at the measured scrollport
 * width so the bar and Save stay in view on a table wider than the window)
 * holding `bar` flush on top and then the padded details. `editor` is the
 * `useExpandRowEditor` return; it renders while the row is open OR closing.
 */
export function RowExpansion({ editor, id, colSpan, panelWidth, testId, bar, children }) {
  if (!editor.isPanelMounted(id)) return null;
  return (
    <InlineEditPanelRow
      colSpan={colSpan}
      closing={editor.isClosing(id)}
      fadeClass={editor.isFading(id) ? 'opacity-0' : 'opacity-100'}
      panelWidth={panelWidth}
      testId={testId}
    >
      {bar}
      {children ? <div className="p-4">{children}</div> : null}
    </InlineEditPanelRow>
  );
}

/** Save Changes / Cancel / Delete at the foot of a PHONE card's panel. */
export function RowEditActions({ onSave, onCancel, onDelete, saving, dirty, testIdPrefix, deleteLabel = 'Delete' }) {
  return (
    <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-200 dark:border-gray-600">
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          data-testid={`${testIdPrefix}-delete`}
          className={`${BTN['danger-soft']} min-h-[44px] md:min-h-0 inline-flex items-center justify-center`}
        >
          {deleteLabel}
        </button>
      )}
      <div className="flex flex-wrap items-center gap-2 ml-auto">
        {dirty && (
          <span className="text-amber-600 dark:text-amber-400 text-xs font-medium" data-testid={`${testIdPrefix}-dirty`}>
            Unsaved changes
          </span>
        )}
        <button
          type="button"
          onClick={onCancel}
          data-testid={`${testIdPrefix}-cancel`}
          className={`${BTN.secondary} min-h-[44px] md:min-h-0 inline-flex items-center justify-center`}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          data-testid={`${testIdPrefix}-save`}
          className={`${BTN.confirm} min-h-[44px] md:min-h-0 inline-flex items-center justify-center`}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

/**
 * The three-way prompt for a held row click. `editor` is the
 * `useExpandRowEditor` return; `noun` names what is unsaved ("This grade").
 */
export function RowEditPrompt({ editor, title, noun, busy }) {
  const held = editor.pending;
  return (
    <ConfirmDialog
      open={held != null}
      title={title}
      message={(
        <>
          <span className="block">
            {noun} has unsaved changes. Discarding puts them back as they were saved.
          </span>
          <span className="block mt-2 font-medium">
            What would you like to do before you {held?.what}?
          </span>
        </>
      )}
      {...UNSAVED_PROMPT_BUTTONS}
      variant="save"
      busy={busy}
      onCancel={editor.keepEditing}
      onAlt={editor.discardAndContinue}
      onConfirm={editor.saveAndContinue}
    />
  );
}
